import { Lightning, Colors, AppData, Router } from '@lightningjs/sdk';
import { translate } from 'support/translate';
import { constants } from 'aliases';
import { HoverableComponent } from 'components/common/HoverableComponent';
import { getImageTextureObj } from 'support/generalUtils';
import ExpandingIconButton, {
  ExpandingIconButtonTemplateSpec,
} from 'components/common/buttons/ExpandingIconButton';
import ActionsList from 'components/common/ActionsList';
import { PageId } from 'types/pageId';
import { getRoutePathFromPageId } from 'support/routerUtils';
import { reportNavBarNavigation } from 'services/analytics/reportingServicePage';
import Page from 'components/Page';
import { RoutePath, WidgetNames } from 'config/routes';
import TextLabel from 'components/common/TextLabel';

export type TopNavSelectedType = 'signin' | 'settings' | null;
export interface TopNavTemplateSpec extends Lightning.Component.TemplateSpec {
  selected: TopNavSelectedType;
  isLoggedIn: boolean;
  pageTitleLabel: string;
  BackgroundGradient: {
    Top: object;
    Middle: object;
    Bottom: object;
  };
  Content: {
    PageTitle: typeof TextLabel;
    Separator: object;
    Actions: typeof ActionsList;
    Logo: Lightning.Texture;
  };
}

const WIDTH = 1920;
const HEIGHT = 120;
const TOP_HEIGHT = HEIGHT * 0.325;
const MIDDLE_HEIGHT = HEIGHT * 0.445;
const BOTTOM_HEIGHT = HEIGHT * 0.23;
const TOP_MIDDLE_TRANSPARENCY = 0.75;
const MIDDLE_BOTTOM_TRANSPARENCY = 0.5;

const LOGO_ICON = 'static/images/brand-logo-white-with-shadow.svg';
const LOGO_TEXTURE = getImageTextureObj(LOGO_ICON, 95, 45);

const SEPARATOR_ICON =
  'static/images/navigation/navigation-selection-indicator-with-shadow.svg';
const SEPARATOR_TEXTURE = getImageTextureObj(SEPARATOR_ICON, 11, 34);

const SETTINGS_ICON_UNFOCUSED =
  'static/images/navigation/top-nav-settings-icon-inactive.svg';
const SETTINGS_ICON_FOCUSED =
  'static/images/navigation/top-nav-settings-icon-active.svg';

const SIGNIN_ICON_UNFOCUSED =
  'static/images/navigation/top-nav-signin-icon-inactive.svg';
const SIGNIN_ICON_FOCUSED =
  'static/images/navigation/top-nav-signin-icon-active.svg';

const INVISIBLE = constants.ui.invisible;
const VISIBLE = 1;
const TRANSPARENT = 0.5;

const SIGNUP_NAV_DATA_KEY = 'signup-signin';
const SETTINGS_NAV_DATA_KEY = 'settings';

const TITLE_FONT_SIZE = 22;
const SEPARATOR_TOP_MARGIN = 6; // Need an offset because separator icon includes dropshadow spacing
const SEPARATOR_LEFT_MARGIN = 10;
const SEPARATOR_RIGHT_MARGIN = 30;
const LOGO_RIGHT_MARGIN = 80;
const GAP = 20;
const BUTTON_H = 48;
const BUTTON_FONT_SIZE = 24;
const PAGE_TITLE_HEIGHT = 28;

export const TOP_NAV_LONG_UP_PRESS_THRESHOLD_MS = 500;

export default class TopNav
  extends HoverableComponent<TopNavTemplateSpec>
  implements Lightning.Component.ImplementTemplateSpec<TopNavTemplateSpec>
{
  private _isLoggedIn = false;
  private _pageTitleLabel = '';
  private _selected: TopNavSelectedType = null;
  private _signInLabel: string | undefined;
  private _settingsLabel: string | undefined;

  private _BackgroundGradient = this.getByRef('BackgroundGradient')!;
  private _Content = this.getByRef('Content')!;
  private _PageTitle = this._Content.getByRef('PageTitle')!;
  private _PageTitleSeparator = this._Content.getByRef('Separator');
  private _Actions = this._Content.getByRef('Actions')!;

  get announce() {
    return [translate('ttsPrompts.topnav'), this.pageTitleLabel];
  }

  get selected() {
    return this._selected;
  }

  set selected(value: TopNavSelectedType) {
    this._selected = value;
  }

  set isLoggedIn(value: boolean) {
    this._isLoggedIn = value;
    this.updateButtons();
  }

  get pageTitleLabel() {
    return this._pageTitleLabel;
  }

  set pageTitleLabel(value: string) {
    this._pageTitleLabel = value;
    this._PageTitle.patch({ text: this._pageTitleLabel });
    this._PageTitleSeparator?.patch({ alpha: value ? VISIBLE : INVISIBLE });
  }

  static override _template(): Lightning.Component.Template<TopNavTemplateSpec> {
    return {
      collision: true,
      BackgroundGradient: {
        flex: { direction: 'column' },
        zIndex: constants.ui.zIndex.navLayer,
        visible: false,
        Top: {
          h: TOP_HEIGHT,
          w: WIDTH,
          colorTop: Colors('background').get(),
          colorBottom: Colors('background')
            .alpha(TOP_MIDDLE_TRANSPARENCY)
            .get(),
          rect: true,
        },
        Middle: {
          h: MIDDLE_HEIGHT,
          w: WIDTH,
          colorTop: Colors('background').alpha(TOP_MIDDLE_TRANSPARENCY).get(),
          colorBottom: Colors('background')
            .alpha(MIDDLE_BOTTOM_TRANSPARENCY)
            .get(),
          rect: true,
        },
        Bottom: {
          h: BOTTOM_HEIGHT,
          w: WIDTH,
          colorTop: Colors('background')
            .alpha(MIDDLE_BOTTOM_TRANSPARENCY)
            .get(),
          colorBottom: Colors('transparent').get(),
          rect: true,
        },
      },
      Content: {
        zIndex: constants.ui.zIndex.navLayer,
        flex: {
          direction: 'row',
          alignItems: 'center',
          justifyContent: 'flex-end',
        },
        x: 0,
        w: 1920,
        h: HEIGHT,
        PageTitle: {
          type: TextLabel,
          text: {
            h: PAGE_TITLE_HEIGHT,
            fontSize: TITLE_FONT_SIZE,
            shadow: true,
            shadowColor: 0xff000000,
          },
        },
        Separator: {
          ...SEPARATOR_TEXTURE,
          flexItem: {
            marginTop: SEPARATOR_TOP_MARGIN,
            marginLeft: SEPARATOR_LEFT_MARGIN,
            marginRight: SEPARATOR_RIGHT_MARGIN,
          },
          alpha: INVISIBLE,
        },
        Actions: {
          type: ActionsList,
          spacing: GAP,
          clipping: true,
          signals: {
            $signInSelected: true,
            $settingsSelected: true,
          },
          items: [],
          alpha: TRANSPARENT,
        },
        Logo: {
          ...LOGO_TEXTURE,
          flexItem: { marginLeft: GAP, marginRight: LOGO_RIGHT_MARGIN },
        },
      },
    };
  }

  updateButtons() {
    const showSignIn =
      AppData?.loginRadiusService !== null && !this._isLoggedIn;
    this._Actions.items = this.getActionButtons(showSignIn);
  }

  getActionButtons(
    showSignIn: boolean,
  ): Lightning.Element.PatchTemplate<ExpandingIconButtonTemplateSpec>[] {
    const getNavData = (slug: string) =>
      AppData?.topNavigationItems?.find(item => item.slug === slug);
    const signInNavData = getNavData(SIGNUP_NAV_DATA_KEY);
    const settingsNavData = getNavData(SETTINGS_NAV_DATA_KEY);

    this._signInLabel = signInNavData?.title;
    this._settingsLabel = settingsNavData?.title;

    const baseButton = {
      type: ExpandingIconButton,
      zIndex: constants.ui.zIndex.navLayer,
      signals: {
        $onHover: true,
        $onUnhover: true,
      },
      reportClick: false,
      height: BUTTON_H,
      fontSize: BUTTON_FONT_SIZE,
      margin: GAP,
      paddingH: GAP,
      backgroundColor: {
        unfocused: Colors('transparent').get(),
      },
    };

    const signIn = {
      ...baseButton,
      label: this._signInLabel,
      action: '$signInSelected',
      selected: this._selected === 'signin',
      passSignals: {
        $signInSelected: true,
      },
      startIcon: {
        focused: SIGNIN_ICON_FOCUSED,
        unfocused: SIGNIN_ICON_UNFOCUSED,
      },
    };

    const settings = {
      ...baseButton,
      label: this._settingsLabel,
      action: '$settingsSelected',
      selected: this._selected === 'settings',
      passSignals: {
        $settingsSelected: true,
      },
      startIcon: {
        focused: SETTINGS_ICON_FOCUSED,
        unfocused: SETTINGS_ICON_UNFOCUSED,
      },
    };

    const buttons: Lightning.Element.PatchTemplate<ExpandingIconButtonTemplateSpec>[] =
      [settings];

    if (showSignIn) buttons.unshift(signIn);

    return buttons;
  }

  override _onActivated() {
    this.updateButtons();
  }

  $signInSelected() {
    Router.navigate(RoutePath.LOGIN);
    reportNavBarNavigation(this._signInLabel!);
  }

  $settingsSelected() {
    Router.navigate(getRoutePathFromPageId(PageId.SETTINGS));
    reportNavBarNavigation(this._settingsLabel!);
  }

  override _getFocused() {
    return this._Actions;
  }

  override _focus() {
    this._BackgroundGradient.patch({
      visible: true,
    });
    this._Actions.patch({ alpha: VISIBLE });
  }

  override _unfocus() {
    this._BackgroundGradient.patch({
      visible: false,
    });
    this._Actions.patch({ alpha: TRANSPARENT });
    // Reset the navbar navigation flag when unfocusing
    if (AppData) {
      AppData.topNavCameFromNavBar = false;
    }
  }

  override _handleBack() {
    Router.focusPage();
  }

  override _handleDown() {
    const activePage = Router.getActivePage() as Page;
    const isNavBarEnabled = activePage?.isEnabledWidget?.(WidgetNames.NAVBAR);
    const isNavBarVisible = activePage?.isNavBarVisible;

    // If we came from the navbar, return to it
    if (AppData?.topNavCameFromNavBar && isNavBarEnabled && isNavBarVisible) {
      Router.focusWidget(WidgetNames.NAVBAR);
      return true;
    }

    // Otherwise, return to the page
    Router.focusPage();
    return true;
  }

  override _handleKey(e: KeyboardEvent) {
    switch (e.key) {
      case 'ArrowDown':
      case 'Escape':
        return false;
    }

    // Same as NavBar, When the handle key event propagates up, it causes the navbar to close. However,
    // we only want the navbar to close to specific key presses defined above. Otherwise,
    // we will stay focused on the navbar
    return true;
  }

  override _handleHover(target: any) {
    if (
      this.alpha < 1 ||
      AppData?.device.hoverDisabled() ||
      // Workaround for preventing hovering when modals are "on top"
      AppData?.modalService.isOtherModalActive(this)
    )
      return true;
    Router.focusWidget('TopNav');
    return true;
  }

  override _handleUnhover() {
    if (
      this.alpha < 1 ||
      AppData?.device.hoverDisabled() ||
      AppData?.modalService.isOtherModalActive(this)
    )
      return true;
    Router.focusPage();
    return false;
  }

  public toggleVisibility(visible?: boolean) {
    if (visible === undefined) {
      visible = this.alpha > 0 ? false : true;
    }

    this.setSmooth('alpha', visible ? 1 : 0);
  }
}
